import * as React from 'react'
import { cn } from '@/lib/utils'
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { ChevronLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

type PageLayoutProps = {
    title: string
    subtitle?: string
    action?: React.ReactNode
    className?: string
    breadcrumbs?: { label: string; href?: string }[]
    children: React.ReactNode
}

export default function PageLayout({ title, subtitle, action, className, breadcrumbs, children }: PageLayoutProps) {
    const navigate = useNavigate()

    const handleBack = React.useCallback(() => {
        const canGoBack =
            typeof window !== 'undefined' &&
            window.history &&
            ((window.history.state && typeof window.history.state.idx === 'number' && window.history.state.idx > 0) ||
                window.history.length > 1)

        if (canGoBack) {
            navigate(-1)
            return
        }

        const hasCrumbs = Array.isArray(breadcrumbs) && breadcrumbs.length > 0
        const prevCrumb = hasCrumbs && breadcrumbs!.length > 1 ? breadcrumbs![breadcrumbs!.length - 2] : undefined
        const target = prevCrumb?.href || (hasCrumbs && breadcrumbs![0]?.href) || undefined
        if (target) navigate(target)
        else navigate(-1)
    }, [breadcrumbs, navigate])

    return (
        <div className={cn('px-4 mt-4 flex flex-col', className)}>
            <div className='bg-white rounded-t-xl pt-4 px-4 py-2 border-b border-gray-200'>
                {breadcrumbs && breadcrumbs.length > 0 && (
                    <div className='mb-2'>
                        <Breadcrumb>
                            <BreadcrumbList>
                                {breadcrumbs.map((bc, idx) => (
                                    <React.Fragment key={idx}>
                                        <BreadcrumbItem>
                                            {bc.href ? (
                                                <BreadcrumbLink href={bc.href}>{bc.label}</BreadcrumbLink>
                                            ) : (
                                                <BreadcrumbPage>{bc.label}</BreadcrumbPage>
                                            )}
                                        </BreadcrumbItem>
                                        {idx < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
                                    </React.Fragment>
                                ))}
                            </BreadcrumbList>
                        </Breadcrumb>
                    </div>
                )}
                <div className='flex items-start justify-between gap-3'>
                    <div className='flex items-center gap-2'>
                        <Button variant='ghost' size='icon' onClick={handleBack} aria-label='Back'>
                            <ChevronLeft className='h-5 w-5' />
                        </Button>
                        <div>
                            <h1 className='text-2xl font-semibold leading-tight'>{title}</h1>
                            {subtitle ? (
                                <p className='text-sm text-muted-foreground mt-1'>{subtitle}</p>
                            ) : null}
                        </div>
                    </div>
                    {action ? <div className='flex-shrink-0'>{action}</div> : null}
                </div>
            </div>
            <div className='bg-white rounded-b-xl p-4 flex-1'>
                {children}
            </div>

        </div>
    )
}


